import React, { useState } from 'react';

const WorkCaseList = ({
  workCases,
  loading,
  filters,
  pagination,
  onFilterChange,
  onPageChange,
  onViewDetail,
  onUpdateStatus,
  onApproveReject,
  currentUser = null
}) => {
  const [expandedCase, setExpandedCase] = useState(null);

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Chờ xử lý', color: 'bg-yellow-100 text-yellow-800' },
      accepted: { label: 'Đã nhận', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Đang thực hiện', color: 'bg-orange-100 text-orange-800' },
      completed: { label: 'Hoàn thành', color: 'bg-green-100 text-green-800' },
      completed: { label: 'Chờ duyệt', color: 'bg-purple-100 text-purple-800' },
      approved: { label: 'Hoàn thành', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'Từ chối', color: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { label: 'Thấp', color: 'text-green-600' },
      medium: { label: 'Trung bình', color: 'text-yellow-600' },
      high: { label: 'Cao', color: 'text-orange-600' },
      urgent: { label: 'Khẩn cấp', color: 'text-red-600' }
    };

    const config = priorityConfig[priority] || { label: priority, color: 'text-gray-600' };
    
    return (
      <span className={`text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Check if deadline is overdue
  const isOverdue = (deadline) => {
    return new Date(deadline) < new Date();
  };

  // Handle status update
  const handleStatusUpdate = async (workCaseId, assigneeId, status, notes = '') => {
    console.log('🔍 [DEBUG] WorkCaseList handleStatusUpdate called with:', {
      workCaseId,
      assigneeId,
      status,
      notes,
      currentUser: currentUser?.id
    });
    await onUpdateStatus(workCaseId, assigneeId, status, notes);
  };

  // Handle approve/reject
  const handleApproveReject = async (workCaseId, action, notes = '') => {
    await onApproveReject(workCaseId, action, notes);
  };

  // Toggle expanded case
  const toggleExpanded = (caseId) => {
    setExpandedCase(expandedCase === caseId ? null : caseId);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-4">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trạng thái</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange({ status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả</option>
              <option value="pending">Chờ xử lý</option>
              <option value="accepted">Đã nhận</option>
              <option value="in_progress">Đang thực hiện</option>
              <option value="completed">Hoàn thành</option>
              <option value="approved">Đã duyệt</option>
              <option value="rejected">Từ chối</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Danh mục</label>
            <select
              value={filters.category}
              onChange={(e) => onFilterChange({ category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả</option>
              <option value="development">Phát triển</option>
              <option value="design">Thiết kế</option>
              <option value="marketing">Marketing</option>
              <option value="support">Hỗ trợ</option>
              <option value="content">Nội dung</option>
              <option value="other">Khác</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Độ ưu tiên</label>
            <select
              value={filters.priority}
              onChange={(e) => onFilterChange({ priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tất cả</option>
              <option value="low">Thấp</option>
              <option value="medium">Trung bình</option>
              <option value="high">Cao</option>
              <option value="urgent">Khẩn cấp</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => onFilterChange({ status: '', category: '', priority: '' })}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Xóa bộ lọc
            </button>
          </div>
        </div>
      </div>

      {/* Work Cases List */}
      <div className="divide-y divide-gray-200">
        {(!workCases || workCases.length === 0) ? (
          <div className="p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Không có work cases</h3>
            <p className="mt-1 text-sm text-gray-500">Bắt đầu tạo work case đầu tiên.</p>
          </div>
        ) : (
          workCases.map((workCase) => (
            <div key={workCase.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {workCase.title}
                    </h3>
                    {getStatusBadge(workCase.status)}
                    {getPriorityBadge(workCase.priority)}
                  </div>

                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {workCase.description}
                  </p>

                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <div>
                      <span className="font-medium">Tạo bởi:</span> {workCase.creator_name}
                    </div>
                    <div>
                      <span className="font-medium">Người nhận:</span> {workCase.assignee_name}
                    </div>
                    <div>
                      <span className="font-medium">Deadline:</span>
                      <span className={isOverdue(workCase.deadline) ? 'text-red-600 font-medium' : ''}>
                        {formatDate(workCase.deadline)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">Tạo lúc:</span> {formatDate(workCase.created_at)}
                    </div>
                  </div>

                  {/* Expanded content */}
                  {expandedCase === workCase.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Chi tiết</h4>
                          <div className="space-y-2 text-sm text-gray-600">
                            <div><span className="font-medium">Danh mục:</span> {workCase.category}</div>
                            <div><span className="font-medium">Mô tả:</span> {workCase.description}</div>
                            <div><span className="font-medium">Trạng thái assignment:</span> {getStatusBadge(workCase.assignment_status)}</div>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Hành động</h4>
                          <div className="space-y-2">
                            {/* Status update buttons for assignee */}
                            {(() => {
                              const normalizeId = (v) => (v == null ? null : Number(v));
                              const isMine = normalizeId(workCase.assignee_id) === normalizeId(currentUser?.id);
                              return isMine && workCase.assignment_status === 'pending';
                            })() && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleStatusUpdate(workCase.id, Number(currentUser?.id), 'accepted')}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                >
                                  Nhận case
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(workCase.id, Number(currentUser?.id), 'rejected')}
                                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                >
                                  Từ chối
                                </button>
                              </div>
                            )}

                            {Number(workCase.assignee_id) === Number(currentUser?.id) && workCase.assignment_status === 'accepted' && (
                              <button
                                onClick={() => handleStatusUpdate(workCase.id, Number(currentUser?.id), 'in_progress')}
                                className="px-3 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700 transition-colors"
                              >
                                Bắt đầu thực hiện
                              </button>
                            )}

                            {Number(workCase.assignee_id) === Number(currentUser?.id) && workCase.assignment_status === 'in_progress' && (
                              <button
                                onClick={() => handleStatusUpdate(workCase.id, Number(currentUser?.id), 'completed')}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                              >
                                Hoàn thành
                              </button>
                            )}

                            {/* Approve/Reject buttons for creator */}
                            {workCase.creator_id === currentUser?.id && (workCase.status === 'on_review' || workCase.status === 'completed') && (
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleApproveReject(workCase.id, 'approve')}
                                  className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                >
                                  Chấp thuận
                                </button>
                                <button
                                  onClick={() => handleApproveReject(workCase.id, 'reject')}
                                  className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                                >
                                  Từ chối
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => toggleExpanded(workCase.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className={`h-5 w-5 transform transition-transform ${expandedCase === workCase.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onViewDetail(workCase.id)}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Chi tiết
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Hiển thị {((pagination.page - 1) * pagination.limit) + 1} đến {Math.min(pagination.page * pagination.limit, pagination.total)} trong tổng số {pagination.total} work cases
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Trước
              </button>
              {[...Array(pagination.totalPages)].map((_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === pagination.page
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sau
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkCaseList;
